import { Link } from "react-router-dom";
import { Bell, CalendarDays, Check, ChevronRight, Circle, Crown, Flame, ScanLine, AlarmClock } from "lucide-react";
import { useAuth } from "@/context/auth";
import { useCategoryCounts, useContacts, useEvents, useGoals, usePromoBanner, useReminders, useScanUsage, useUnread } from "@/hooks/queries";
import { fmtDate, greeting, promoUri } from "@/lib/toto";
import ContactRow from "@/components/ContactRow";
import { Page } from "@/components/kit";
import HomeDesktop from "@/pages/HomeDesktop";
import { PremiumBadge, useIsPremium } from "@/components/PremiumBadge";

export function EventRow({ ev }) {
  return (
    <Link to={`/events/${ev.id}`} className="t-card flex items-center gap-3 p-3.5 hover:shadow-[0_6px_20px_-12px_rgba(15,23,42,.25)] transition-shadow" data-testid={`event-row-${ev.id}`}>
      <span className="w-11 h-11 rounded-[12px] bg-[#E5E5FD] flex items-center justify-center shrink-0"><CalendarDays className="w-5 h-5 text-[#0000EC]" strokeWidth={2.4} /></span>
      <span className="flex-1 min-w-0">
        <span className="block text-[16px] font-bold truncate">{ev.name}</span>
        <span className="block text-[14px] text-[#64748B] truncate">{fmtDate(ev.eventDate)} · {ev.contactsCount} contact{ev.contactsCount === 1 ? "" : "s"}{ev.location ? ` · ${ev.location}` : ""}</span>
      </span>
      <ChevronRight className="w-5 h-5 text-[#475569]" />
    </Link>
  );
}

const Stat = ({ value, label, bg, fg, testid, to }) => (
  <Link to={to} className="rounded-[18px] py-4 flex flex-col items-center transition-transform hover:-translate-y-0.5" style={{ background: bg }} data-testid={testid}>
    <span className="text-[24px] font-extrabold" style={{ color: fg }}>{value}</span>
    <span className="text-[14px] font-semibold text-[#475569]">{label}</span>
  </Link>
);

function GoalsCard({ goals }) {
  const daily = goals?.goals?.daily ?? [];
  const done = daily.filter((g) => g.completed).length;
  const streak = goals?.streak?.currentStreak ?? 0;
  return (
    <Link to="/goals" className="block rounded-[20px] bg-[#F5F3FF] border border-[#EDE9FE] p-4" data-testid="home-goals-card">
      <div className="flex items-center justify-between">
        <span className="text-[15px] font-bold text-[#7C3AED]">{done} / {daily.length} completed</span>
        {streak > 0 && <span className="flex items-center gap-1 rounded-full bg-[#FEF9C3] px-2.5 py-1 text-[13px] font-bold text-[#B45309]"><Flame className="w-3.5 h-3.5 fill-[#EA580C] text-[#EA580C]" />{streak} day streak</span>}
      </div>
      <div className="mt-2.5 h-2 rounded-full bg-white overflow-hidden"><div className="h-full bg-[#7C3AED] rounded-full transition-[width] duration-500" style={{ width: `${daily.length ? (done / daily.length) * 100 : 0}%` }} /></div>
      <ul className="mt-3 space-y-1.5">
        {daily.slice(0, 4).map((g) => (
          <li key={g.key} className={`flex items-center gap-2 text-[15px] ${g.completed ? "font-bold text-[#059669]" : "text-[#334155]"}`}>
            {g.completed ? <Check className="w-4 h-4" strokeWidth={3} /> : <Circle className="w-3 h-3" />}{g.title}
          </li>
        ))}
        {!daily.length && <li className="text-[14px] text-[#64748B]">Your goals will appear here.</li>}
      </ul>
    </Link>
  );
}

export default function Home() {
  const { user } = useAuth();
  const usage = useScanUsage().data;
  const promo = usePromoBanner().data;
  const counts = useCategoryCounts().data;
  const contacts = useContacts().data ?? [];
  const events = useEvents({ sort: "date" }).data ?? [];
  const goals = useGoals().data;
  const due = (useReminders("pending").data ?? []).filter((r) => r.overdue || r.dueToday);
  const premium = useIsPremium();
  const unread = useUnread().data?.count ?? 0;
  const usedCats = (counts?.categories ?? []).filter((c) => c.count > 0);
  const promoImg = promo?.enabled ? promoUri(promo.imageUrl) : null;
  const limit = usage?.freeScanLimit ?? 25;

  return (
    <>
    <HomeDesktop user={user} usage={usage} promoImg={promoImg} promo={promo} counts={counts} contacts={contacts} events={events} goals={goals} due={due} />
    <Page className="pt-4 lg:hidden">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[16px] text-[#475569]">{greeting()}</p>
          <h1 className="text-[28px] lg:text-4xl font-extrabold" data-testid="home-user-name">{user?.name?.split(" ")[0] ?? "there"}</h1>
          {premium && <div className="mt-1"><PremiumBadge testid="home-premium-badge" /></div>}
        </div>
        <Link to="/notifications" className="lg:hidden relative w-12 h-12 rounded-full border border-[#E2E8F0] flex items-center justify-center hover:bg-[#F8FAFC]" data-testid="home-notifications">
          <Bell className="w-[22px] h-[22px]" strokeWidth={2} />
          {unread > 0 && <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[11px] font-bold flex items-center justify-center" data-testid="home-unread-badge">{unread}</span>}
        </Link>
      </div>

      <div className="lg:grid lg:grid-cols-[1.25fr_1fr] lg:gap-8">
        <div>
          {promoImg && (
            <a href={promo.linkUrl || undefined} target="_blank" rel="noreferrer" className="block mt-5 rounded-[20px] overflow-hidden relative" data-testid="home-ad-banner">
              <img src={promoImg} alt={promo.title || "Promotion"} className="w-full aspect-[2.6/1] object-cover" />
            </a>
          )}
          <Link to="/scan" className="mt-6 flex items-center gap-4 rounded-[20px] p-4 text-white bg-gradient-to-r from-[#0000EC] to-[#4338CA] shadow-[0_14px_30px_-14px_rgba(0,0,236,.8)] transition-transform hover:-translate-y-0.5" data-testid="home-scan-cta">
            <span className="w-12 h-12 rounded-[12px] bg-white/15 flex items-center justify-center"><ScanLine className="w-6 h-6" strokeWidth={2.4} /></span>
            <span className="flex-1"><span className="block text-[19px] font-bold">Scan a Card</span><span className="block text-[14px] text-white/85">Turn every card into a connection</span></span>
            <ChevronRight className="w-6 h-6" />
          </Link>
          {usage && !usage.isPremium && (
            <Link to="/subscription" className="mt-4 block t-card p-4" data-testid="scan-usage-card">
              <div className="flex justify-between text-[14px]"><span className="font-semibold text-[#475569]">Free Scans</span><span className="font-bold" data-testid="scan-usage-count">{usage.scansUsed} / {limit}</span></div>
              <div className="mt-2 h-2 rounded-full bg-[#F1F5F9] overflow-hidden"><div className="h-full bg-[#0000EC]" style={{ width: `${Math.min(100, (usage.scansUsed / limit) * 100)}%` }} /></div>
              <p className="mt-2 flex items-center gap-1 text-[13px] text-[#7C3AED] font-semibold"><Crown className="w-4 h-4" />Go Premium for unlimited scans</p>
            </Link>
          )}
          <div className="grid grid-cols-3 gap-3 mt-6">
            <Stat value={counts?.total ?? contacts.length} label="Contacts" bg="#EFF6FF" fg="#2563EB" testid="home-stat-contacts" to="/contacts" />
            <Stat value={events.length} label="Events" bg="#FFF7ED" fg="#EA580C" testid="home-stat-events" to="/events" />
            <Stat value={usedCats.length} label="Categories" bg="#F5F3FF" fg="#7C3AED" testid="home-stat-categories" to="/contacts" />
          </div>
          {due.length > 0 && (
            <Link to={`/contacts/${due[0].contactId}`} className="mt-6 flex items-center gap-3 rounded-[18px] bg-[#FFF7ED] border border-[#FED7AA] p-4" data-testid="home-due-reminders">
              <AlarmClock className="w-6 h-6 text-[#EA580C]" />
              <span className="flex-1 text-[15px] font-semibold text-[#C2410C]">{due.length} follow-up{due.length > 1 ? "s" : ""} due — {due[0].contactName || "a contact"}</span>
              <ChevronRight className="w-5 h-5 text-[#C2410C]" />
            </Link>
          )}
          <div className="flex items-center justify-between mt-8 mb-3"><h2 className="t-section">Today's Goals</h2><Link to="/goals" className="t-link" data-testid="home-view-goals">View Goals →</Link></div>
          <GoalsCard goals={goals} />
        </div>
        <div>
          <div className="flex items-center justify-between mt-8 mb-3"><h2 className="t-section">Recent Events</h2><Link to="/events" className="t-link" data-testid="home-view-events">View All →</Link></div>
          <div className="space-y-3">
            {events.slice(0, 3).map((ev) => <EventRow key={ev.id} ev={ev} />)}
            {!events.length && <p className="text-[14px] text-[#64748B]">No events yet. Create one to group your contacts.</p>}
          </div>
          {usedCats.length > 0 && (
            <>
              <h2 className="t-section mt-8 mb-3">Categories</h2>
              <div className="grid grid-cols-2 gap-3">
                {usedCats.slice(0, 6).map((c) => (
                  <Link key={c.id} to={`/contacts?categoryId=${c.id}`} className="t-card p-3.5 hover:border-[#0000EC]/40 transition-colors" data-testid={`home-category-${c.slug}`}>
                    <span className="block text-[15px] font-bold truncate">{c.name}</span>
                    <span className="text-[13px] text-[#64748B]">{c.count} contact{c.count === 1 ? "" : "s"}</span>
                  </Link>
                ))}
              </div>
            </>
          )}
          <div className="flex items-center justify-between mt-8 mb-3"><h2 className="t-section">Recent Contacts</h2><Link to="/contacts" className="t-link">View All →</Link></div>
          <div className="space-y-3">
            {contacts.slice(0, 5).map((c) => <ContactRow key={c.id} contact={c} />)}
            {!contacts.length && <p className="text-[14px] text-[#64748B]" data-testid="home-contacts-empty">No contacts yet. Scan your first visiting card.</p>}
          </div>
        </div>
      </div>
    </Page>
    </>
  );
}
